import {
  Container,
  SpaceBetween,
  Alert,
  ContentLayout,
  Button,
  Spinner,
  Header,
  Box,
  KeyValuePairs,
  KeyValuePairsProps,
} from "@amzn/awsui-components-console";
import React, { useCallback, useEffect, useRef, useState } from "react";
import { TextContent } from "@amzn/awsui-components-console";
import { useAppDispatch, useAppSelector } from "src/redux/hooks";
import { resetFormsState } from "src/forms/formsSlice";
import { resetWizardState } from "src/components/wizard/wizardSlice";
import { resetReviewPageState } from "src/forms/reviewPage/reviewPageSlice";
import { resetSitesState } from "src/sites/sitesSlice";
import SimLinkGenerator from "src/components/simLinkGenerator/SimLinkGenerator";
import { SIM_TYPES } from "src/components/simLinkGenerator/types";
import { ClaimSubmittedStatus, LineOfBusiness } from "../app/types";
import { CARGO_STAGES } from "../app/Cargo/types";
import { useNavigate } from "react-router-dom";
import { ROUTES } from "../app/constants";
import { setValueInAppState } from "../app/appSlice";
import { RootState } from "src/redux/store";
import { useClaimSubmissionState } from "./hooks/useClaimSubmissionState";
import { useCargoTimeoutWorkaround } from "./hooks/useCargoTimeoutWorkaround";
import {
  buildSubmittedDataFromResponse,
  getUnknownErrorData,
} from "./claimSubmittedUtils";

export interface ClaimSubmittedProps {
  onBackToEdit: () => void;
  onStartNewClaim: () => void;
  claimModel: unknown;
}

const SUCCESS_MESSAGES_CARGO: Record<string, React.ReactNode> = {
  [CARGO_STAGES.STAGE1]: (
    <Box variant="p">
      Stage one of the submission process is complete. If you are ready to
      provide information concerning the damaged/missing items, please click
      Continue. If you are not ready you will have received an email
      confirmation containing a link which you can use to come back and continue
      with your claim submission
    </Box>
  ),
  [CARGO_STAGES.STAGE2]: (
    <Box>
      <Box variant="p">
        Thank you for updating your claim with details of the damaged/missing
        items.
      </Box>
      <Box variant="p">
        Stage two of your claim submission is complete. If you are now ready to
        provide your supporting claim documentation to finalize your claim,
        please click Continue. If you do not have your documentation ready you
        can come back and upload later by using the link in the original email
        you received.
      </Box>
      <Box variant="p">
        As a reminder, we will need both the Commercial Invoice and Bill of
        Lading as the minimum documents. It will also be very helpful if you can
        provide as many additional documents as possible, for example: Photos,
        Proff of Delivery/Arrival Receipt, Proof from Carrier/LSP, Police Report
        and/or any other documents.
      </Box>
    </Box>
  ),
  [CARGO_STAGES.STAGE3]: (
    <Box>
      <Box variant="p">Thank you for uploading your claim documentation!</Box>
      <Box variant="p">
        Your claim submission is now complete. You will soon be contacted by one
        of our claim experts.
      </Box>
    </Box>
  ),
};

const PAGE_TITLES: Record<ClaimSubmittedStatus, string> = {
  [ClaimSubmittedStatus.SUCCESS]: "Claim Submitted",
  [ClaimSubmittedStatus.LOADING]: "Claim Submission in Progress",
  [ClaimSubmittedStatus.ERROR]: "Error Submitting Claim",
};

function ClaimSubmitted({
  onBackToEdit,
  onStartNewClaim,
  claimModel,
}: ClaimSubmittedProps) {
  const {
    response,
    status,
    lineOfBusiness,
    workersCompensationTpa,
    amazonClaimReferenceId,
  } = useClaimSubmissionState();
  const { currentStage } = useAppSelector((state: RootState) => state.cargoApp);

  const [submittedData, setSubmittedData] = useState<
    KeyValuePairsProps.Pair[] | undefined
  >(undefined);
  const [claimSubmittedTitleText, setClaimSubmittedTitleText] =
    useState<React.ReactNode>(null);

  const navigate = useNavigate();
  const dispatch = useAppDispatch();

  // Track whether we already reset app state for this success render
  const hasResetRef = useRef(false);

  const stableSetSubmittedData = useCallback(
    (data: KeyValuePairsProps.Pair[]) => setSubmittedData(data),
    []
  );

  const { isTimeoutWorkaroundActive } = useCargoTimeoutWorkaround({
    lineOfBusiness,
    claimModel,
    setSubmittedData: stableSetSubmittedData,
  });

  // Reset app state once when status transitions to success
  useEffect(() => {
    if (status === ClaimSubmittedStatus.SUCCESS && !hasResetRef.current) {
      hasResetRef.current = true;
      dispatch(resetFormsState());
      dispatch(resetWizardState());
      dispatch(resetReviewPageState());
      dispatch(resetSitesState());
      dispatch(setValueInAppState({ key: "claimSubmitSuccess", value: true }));
    }
  }, [status, dispatch]);

  // Process API response into display data
  useEffect(() => {
    if (!response?.data) return;
    const result = buildSubmittedDataFromResponse(
      response,
      claimModel,
      workersCompensationTpa
    );
    setSubmittedData(result.data);
    setClaimSubmittedTitleText(result.titleText);
  }, [response, claimModel, workersCompensationTpa]);

  // Focus on page header for accessibility
  useEffect(() => {
    const header = document.querySelector('[title="Claim Submitted Header"]');
    if (header) {
      (header as HTMLElement).tabIndex = 0;
      (header as HTMLElement).focus();
      window.scrollTo({ top: 0, behavior: "smooth" });
    }
  }, []);

  const renderWarnings = () => {
    if (!response?.warningList?.length) return null;
    return (
      <Alert
        type="warning"
        data-testid="claim-submitted-warning-alert"
        header="Warnings!"
      >
        <div role="alert">
          <TextContent>
            <ul>
              {response.warningList.map((warning, index) => (
                <li key={index}>{warning}</li>
              ))}
            </ul>
          </TextContent>
        </div>
      </Alert>
    );
  };

  const renderLoading = () => (
    <Box textAlign="center">
      <SpaceBetween size="xxl" direction="vertical">
        <Alert
          header={isTimeoutWorkaroundActive ? "Processing..." : "Loading..."}
          data-testid="claim-submitted-loading-alert"
        >
          <span role="alert">
            {isTimeoutWorkaroundActive ? (
              <>
                Your claim processing is taking longer than expected. Please do
                not navigate away from this screen as we continue to process
                your submission. We are checking the status of your claim and
                will update you shortly.
              </>
            ) : (
              <>
                Thank you for submitting the claim. Please wait on this screen
                while we load the claim details. You will also receive a
                confirmation email shortly.
              </>
            )}
          </span>
        </Alert>
        <br />
        <Spinner data-testid="claim-submitted-spinner" size="large" />
      </SpaceBetween>
    </Box>
  );

  const renderSuccess = () => {
    const cargoClaimNumber = submittedData?.find(
      (item) => item.label === "Claim Reference Number"
    )?.value;

    // For IPL claims, append Amazon Claim Reference ID if not already present
    let displayData = submittedData;
    if (lineOfBusiness === LineOfBusiness.IPL && submittedData) {
      const hasAmazonRefId = submittedData.some(
        (item) => item.label === "Amazon Claim Reference ID"
      );
      if (!hasAmazonRefId && amazonClaimReferenceId) {
        displayData = [
          ...submittedData,
          {
            label: "Amazon Claim Reference ID",
            value: amazonClaimReferenceId,
          },
        ];
      }
    }

    return (
      <Box textAlign="center">
        <SpaceBetween size="xxl" direction="vertical">
          <SpaceBetween size="m" direction="vertical">
            <Alert
              type="success"
              header="Success!"
              data-testid="claim-submitted-success-alert"
            >
              <span role="alert">
                Your claim has been successfully submitted! Please see the
                details below. You will be receiving a confirmation email
                shortly.
              </span>
            </Alert>
            {renderWarnings()}
          </SpaceBetween>
          <SpaceBetween size="m" direction="vertical">
            <Container
              data-testid="claim-submitted-success-data-grid"
              header={
                <Header variant="h2" data-testid="data-grid-header">
                  Claim Information
                </Header>
              }
            >
              <KeyValuePairs
                columns={1}
                items={displayData?.length ? displayData : []}
              />
              {lineOfBusiness === LineOfBusiness.CARGO
                ? SUCCESS_MESSAGES_CARGO[currentStage]
                : null}
            </Container>
          </SpaceBetween>
          <Box textAlign="right">
            <span style={{ paddingRight: "5px" }}>
              <Button
                variant="normal"
                onClick={onStartNewClaim}
                data-testid="claim-submitted-start-new-claim-button"
              >
                Start a New Claim
              </Button>
            </span>
            {cargoClaimNumber &&
            lineOfBusiness === LineOfBusiness.CARGO &&
            currentStage !== CARGO_STAGES.STAGE3 ? (
              <span style={{ paddingLeft: "5px" }}>
                <Button
                  variant="primary"
                  onClick={() => {
                    onStartNewClaim();
                    navigate(
                      `${ROUTES.CARGO_CLAIM_INTAKE_FORM}?claimNumber=${cargoClaimNumber}`
                    );
                  }}
                  data-testid="claim-submitted-continue-claim"
                >
                  Continue
                </Button>
              </span>
            ) : null}
          </Box>
        </SpaceBetween>
      </Box>
    );
  };

  const renderError = () => {
    const alertText = (
      <div role="alert">
        {claimSubmittedTitleText ??
          "Sorry, your claim submission was unsuccessful. Please correct the errors shown below and resubmit the form."}
      </div>
    );
    return (
      <Box textAlign="center">
        <SpaceBetween size="xxl" direction="vertical">
          <Alert
            type="error"
            header="Error"
            data-testid="claim-submitted-error-alert"
          >
            {alertText}
          </Alert>
          <Container
            data-testid="claim-submitted-error-data-grid"
            header={
              <Header variant="h2" data-testid="data-grid-header">
                Error Details
              </Header>
            }
          >
            <KeyValuePairs
              columns={1}
              items={
                !submittedData || !submittedData.length
                  ? getUnknownErrorData(claimModel, workersCompensationTpa)
                  : submittedData
              }
            />
          </Container>
          <Box textAlign="right">
            <span style={{ paddingRight: "5px" }}>
              <Button
                onClick={onBackToEdit}
                data-testid="claim-submitted-back-to-edit-button"
              >
                Back to edit
              </Button>
            </span>
            <span style={{ paddingLeft: "5px" }}>
              <Button
                variant="primary"
                onClick={onStartNewClaim}
                data-testid="claim-submitted-start-new-claim-button"
              >
                Start a New Claim
              </Button>
            </span>
          </Box>
        </SpaceBetween>
      </Box>
    );
  };

  const renderContents = () => {
    if (status === ClaimSubmittedStatus.LOADING) return renderLoading();
    if (status === ClaimSubmittedStatus.SUCCESS) return renderSuccess();
    if (status === ClaimSubmittedStatus.ERROR) return renderError();
    return null;
  };

  return (
    <>
      <ContentLayout
        header={
          <SpaceBetween size="m">
            <Header variant="h1">
              <div title="Claim Submitted Header">
                {status ? PAGE_TITLES[status] : ""}
              </div>
            </Header>
          </SpaceBetween>
        }
      >
        <Container>{renderContents()}</Container>
      </ContentLayout>
      {status &&
      [ClaimSubmittedStatus.SUCCESS, ClaimSubmittedStatus.LOADING].includes(
        status
      ) &&
      lineOfBusiness === LineOfBusiness.WORKERS_COMPENSATION ? (
        <Box textAlign="left" padding={{ left: "xs", top: "xs" }}>
          <TextContent>
            If you need to make any updates to the claim, please follow the
            existing process by creating a{" "}
            <SimLinkGenerator
              ariaLabel="Pressing this link will open a new tab where you will be able to create a ticket to modify the submitted claim if necessary."
              target="_blank"
              claimModel={claimModel}
              simType={SIM_TYPES.CLAIM_SUBMITTED}
            >
              ticket.
            </SimLinkGenerator>
            <br />
          </TextContent>
        </Box>
      ) : null}
    </>
  );
}

export default ClaimSubmitted;
